import React, { useContext } from 'react';

import { Typography } from 'antd';
import styled from 'styled-components';

import LinearGradientDivider from 'components/lib/LinearGradientDivider';
import { COMMUNITY_RENTAL_TYPE_LABELS } from 'constants/rentalTypes';
import { useAppSelector } from 'store/hook';

import BasicInfoEditModal from './BasicInfoEditModal';
import BasicInfoLifePlanDetails from './BasicInfoLifePlanDetails';
import Header from '../../common/header/Header';
import { CommunityInfoContext, useCommunityInfoContext } from '../../CommunityInfoContext';
import CompetitorsTable from '../competitors/CompetitorsTable';
import { formatUrl } from '../utils';

const { Paragraph } = Typography;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 15px;
`;

const Label = styled.p`
  color: var(--text-primary);
  font-family: var(--font-bold);
  width: 50%;
`;

const Text = styled.p`
  color: var(--text-primary);
  font-size: 14px;
  font-family: var(--font-regular);
  width: 50%;
  text-align: right;
`;

const Link = styled.a`
  color: var(--dark-blue);
  font-size: 14px;
  font-family: var(--font-regular);
  width: 60%;
  text-align: right;
  word-wrap: break-word;
`;

const Notes = styled(Paragraph)`
  font-size: 14px;
  color: var(--text-primary);
  white-space: break-spaces;
  margin-top: 5px;
`;

const EllipsisSymbol = styled.span`
  color: var(--dark-blue);
  cursor: pointer;
`;

const StyledLinearGradientDivider = styled(LinearGradientDivider)`
  margin-top: 15px;
`;

const BasicInfo = () => {
  const { currentUser } = useAppSelector((state) => state.auth);
  const hasPricingDataAccess = Boolean(currentUser?.isSuperuser || currentUser?.feature_flags?.view_pricing_data);
  const { loaded, isCompetitor, communityInfo, communityInfoRefetch } = useContext(CommunityInfoContext);

  const { mappedCareData } = useCommunityInfoContext();

  const careTypes = mappedCareData
    .filter((careType) => careType.communityFees.length || careType.fees.length || careType.livingInfo.length)
    .map((careType) => careType.type)
    .join(', ');

  const communityType = communityInfo?.rental_types.map((type) => COMMUNITY_RENTAL_TYPE_LABELS[type.type]).join(', ');

  const showEditButton = hasPricingDataAccess && !isCompetitor && loaded && currentUser?.isSuperuser;
  return (
    <Container>
      <Header
        title="Basic Info"
        controls={
          <BasicInfoEditModal
            type={'button'}
            show={showEditButton}
            buttonDataTestId="cd-edit-basic-info-btn"
            onSuccess={communityInfoRefetch}
          />
        }
      />
      {communityInfo && (
        <Content>
          <Row>
            <Label>Community Name</Label>
            <Label style={{ textAlign: 'right' }} data-test-id="cd-comm-name-val">
              {communityInfo?.comunity_name}
            </Label>
          </Row>
          <Row>
            <Label>Community Type</Label>
            <Text data-test-id="cd-comm-type-val">{communityType}</Text>
          </Row>
          <BasicInfoLifePlanDetails></BasicInfoLifePlanDetails>
          <Row>
            <Label>Care Types</Label>
            <Text data-test-id="cd-care-types-val">{careTypes}</Text>
          </Row>
          <StyledLinearGradientDivider></StyledLinearGradientDivider>
          {!isCompetitor && (
            <span>
              <Row>
                <Label>Company</Label>
                <Text data-test-id="cd-company-val">{communityInfo?.company?.name}</Text>
              </Row>
              <StyledLinearGradientDivider></StyledLinearGradientDivider>
            </span>
          )}
          <Row>
            <Label>Address</Label>
            <Text data-test-id="cd-address-val">{communityInfo?.address}</Text>
          </Row>
          <Row>
            <Label>Phone Number</Label>
            <Link data-test-id="cd-phone-val">{communityInfo?.phone_number}</Link>
          </Row>
          <Row>
            <Label>Website</Label>
            <Link
              data-test-id="cd-website-val"
              href={formatUrl(communityInfo?.website)}
              target="_blank"
              rel="noopener noreferrer"
            >
              {communityInfo?.website}
            </Link>
          </Row>
          <Row>
            <Label>Year Opened</Label>
            <Text data-test-id="cd-year-val">{communityInfo?.year_opened}</Text>
          </Row>
          <Row>
            <Label>Google Place ID</Label>
            <Link data-test-id="cd-google-place-id">{communityInfo?.google_map_location}</Link>
          </Row>
          <Row>
            <Label>A Place For Mom ID</Label>
            <Link
              data-test-id="cd-apfm-id"
              href={` https://www.aplaceformom.com/community/${communityInfo?.a_place_for_mom_id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {communityInfo?.a_place_for_mom_id}
            </Link>
          </Row>
          <Row>
            <Label>Caring Url</Label>
            <Link
              data-test-id="cd-caring-id"
              href={formatUrl(communityInfo?.caring_url)}
              target="_blank"
              rel="noopener noreferrer"
            >
              {communityInfo?.caring_url}
            </Link>
          </Row>
          {communityInfo.notes && (
            <span>
              <Row>
                <Label>Community Notes</Label>
              </Row>
              <Notes
                data-test-id="cd-community-notes-val"
                ellipsis={{
                  rows: 4,
                  expandable: 'collapsible',
                  symbol: (expanded: boolean) => (
                    <EllipsisSymbol>{expanded ? 'Read Less' : 'Read More'}</EllipsisSymbol>
                  ),
                }}
              >
                {communityInfo.notes}
              </Notes>
            </span>
          )}
          {currentUser?.isSuperuser && (
            <>
              <StyledLinearGradientDivider></StyledLinearGradientDivider>
              <Row>
                <Label style={{ marginBottom: '10px' }}>Competitors</Label>
              </Row>
              <CompetitorsTable></CompetitorsTable>
            </>
          )}
        </Content>
      )}
    </Container>
  );
};

export default BasicInfo;
